<template>
  <div>
    <b-row class="d-flex">
      <b-col class="d-flex justify-content-end button-group">
        <b-popover target="invoice-creating" :show.sync="popoverCreateWithStatus" placement="bottom"
                   ref="invoice-creating-popover">
          <div v-for="(invoice, idx) in independent_si_types" :key="invoice.key" :class="{'mt-2': idx !== 0}">
            <b-btn size="sm" class="full-width" :title="`Create ${invoice.key} invoice`" @click="create(invoice.key)">
              {{invoice.value}}
            </b-btn>
          </div>
        </b-popover>
        <img v-if="currentRoutePermissions.add_invoice"
             src="@/assets/images/common-icons/icon_create.svg"
             id="invoice-creating"
        >
        <img
          v-if="currentRoutePermissions.delete_invoice"
          src="@/assets/images/common-icons/icon_delete.svg" @click="deleteInvoices"
          :title="$t('buttons.sales_invoices.delete_invoices')">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </b-col>
    </b-row>
    <div class="d-flex align-content-center justify-content-between mt-1">
      <b-col cols="7" class="d-flex px-0">
        <div class="mr-3">
          <label class="mr-3 mb-0 text-nowrap">{{$t('labels.common.table_templates')}}</label>
          <multiselect v-model="template" :max-height="200"
                       placeholder="" label="name" track-by="name"
                       :options="invoicesGridTemplates" :multiple="false"
                       :close-on-select="true" @input="applyTemplate"
                       :disabled="!salesInvoices.length"
                       class="invoice-grid-templates"
                       select-label="" deselect-label="" selected-label=""
          />
        </div>
        <div class="d-flex mt-3">
          <div class="d-flex align-items-center">
            <b-btn class="mr-2" size="sm" title="Create new template" style="margin-left: 0" :disabled="!salesInvoices.length"
                   @click="createTemplate">
              {{$t('buttons.common.create')}}
            </b-btn>
            <b-btn class="mr-2" size="sm" title="Delete selected template" variant="outline-danger" :disabled="!template"
                   @click="deleteSelectedTemplate">
              {{$t('buttons.common.delete')}}
            </b-btn>
            <b-btn class="mr-2" size="sm" title="Update selected template" variant="outline-success" :disabled="!template"
                   @click="updateSelectedTemplate">
              {{$t('buttons.common.update')}}
            </b-btn>
          </div>
        </div>
      </b-col>
      <b-col cols="2" class="px-0">
        <label>{{$t('labels.common.report_date')}}</label>
        <datepicker v-model="reportDate" lang="en" :first-day-of-week="1"
                    placeholder="" />
      </b-col>
      <b-col cols="2" class="p-0 excel-reports">
        <label class="mr-3 mb-0 text-nowrap">{{$t('labels.common.excel_reports')}}</label>
        <multiselect v-model="excelReport" :max-height="200"
                     placeholder="" label="value" track-by="value"
                     :options="salesInvoicesExports" :multiple="false"
                     :close-on-select="true"
                     select-label="" deselect-label="" selected-label=""
                     @select="selectSalesInvoicesReport"
        />
      </b-col>
    </div>
    <div class="search-form">
      <create-sales-invoice-modal :data="createInvoiceData"/>
      <div class="d-flex justify-content-between toggle-container mt-3" @click="showCollapse = !showCollapse"
           :aria-expanded="showCollapse"
           aria-controls="collapse">
        <h3>
          {{$t('common_content.advanced_search')}}
        </h3>
        <div class="icons-wrapper">
          <icon name="angle-down" class="angle" v-if="showCollapse"/>
          <icon name="angle-right" class="angle" v-else/>
        </div>
      </div>
      <b-collapse v-model="showCollapse" id="collapse">
        <div class="search-form__filters">
          <b-row>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="searchData.buyers" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="buyers" :multiple="true"
                           :close-on-select="false"/>
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.invoice_number')}}</label>
              <input class="form-control" v-model="searchData.invoice_number" @keyup.enter="search">
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.official_sum_payment_status')}}</label>
              <multiselect v-model="searchData.paid_status_official" :max-height="200"
                           placeholder="" :multiple="true" :close-on-select="false"
                           label="name" track-by="key"
                           :options="paymentStatuses"/>
            </b-col>
            <b-col>
              <label>{{$t('labels.partner.country')}}</label>
              <multiselect v-model="searchData.buyer_country_code" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="countries" :multiple="true"
                           :close-on-select="false"/>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.seller')}}</label>
              <multiselect v-model="searchData.sellers" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="sellers" :multiple="true" :close-on-select="false"
                           select-label="" deselect-label="" selected-label=""/>
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.invoice_status')}}</label>
              <multiselect v-model="searchData.status" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="invoiceStatuses" :multiple="true" :close-on-select="false"
                           select-label="" deselect-label="" selected-label=""/>
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.additional_sum_payment_status')}}</label>
              <multiselect v-model="searchData.paid_status_additional" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="paymentStatuses" :multiple="true" :close-on-select="false"
                           select-label="" deselect-label="" selected-label=""/>
            </b-col>
            <b-col>
              <label>{{$t('labels.sale_order.stock_address')}}</label>
              <multiselect v-model="searchData.stock_addresses" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="stock_addresses" :multiple="true"
                           :close-on-select="false"/>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.date_type')}}</label>
              <multiselect v-model="searchData.type_date" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="date_types" :multiple="false" :close-on-select="true"
                           select-label="" deselect-label="" selected-label=""/>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.invoicing_type')}}</label>
              <multiselect v-model="searchData.invoicing_type" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="invoicing_types" :multiple="true" :close-on-select="false"
                           select-label="" deselect-label="" selected-label=""/>
            </b-col>
            <b-col class="checkbox-wrapper">
              <label for="overdue">{{$t('labels.common.overdue')}}</label>
              <input id="overdue" type="checkbox" v-model="searchData.overdue">
            </b-col>
            <b-col>
              <label>{{$t('labels.common.creator')}}</label>
              <multiselect v-model="searchData.user" :max-height="200"
                           placeholder="" label="username" track-by="username"
                           :options="users" :multiple="true" :close-on-select="false"
                           select-label="" deselect-label="" selected-label=""/>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="3">
              <label>{{$t('labels.invoices.invoice_date')}}</label>
              <datepicker v-model="searchData.invoice_date" range range-separator="/" placeholder=""
                          lang="en" :first-day-of-week="1" :disabled="isTypeDateEmpty"/>
            </b-col>
            <b-col cols="3">
              <label>{{$t('labels.invoices.invoice_type')}}</label>
              <multiselect v-model="searchData.type" :max-height="200"
                           placeholder="" label="value" track-by="value"
                           :options="si_types" :multiple="true"
                           :close-on-select="false"/>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
      <div>
        <b-row>
          <b-col cols="4">
            <input class="form-control"
                   placeholder="Filter..."
                   @input="setQuickFilter">
          </b-col>
          <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
            <label for="pagination-toggle" class="mr-1">Pagination</label>
            <input id="pagination-toggle" type="checkbox"
                   title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                   @change="togglePagination">
            <save-columns-btn :grid="salesInvoicesGridOptions" :grid-name="salesInvoicesGridName" class="ml-1"/>
          </b-col>
        </b-row>
      </div>
    </div>
    <div>
      <b-row>
        <b-col>
          <ag-grid-vue class="ag-theme-balham" :gridOptions="salesInvoicesGridOptions"
                       @selectionChanged="selectionChangedInvoices"
                       :pinnedBottomRowData="salesInvoicesTotalData"
                       :style="gridHeight" :rowData="salesInvoices" :columnDefs="salesInvoicesColumns"
                       @rowDoubleClicked="redirectToInvoice" :postProcessPopup="postProcessPopup"
                       @gridReady="gridReady">
          </ag-grid-vue>
        </b-col>
      </b-row>
      <b-row class="pagination__wrapper" v-if="paginationState">
        <b-col cols="5">
          <div class="pagination-info">{{countOfViewSalesInvoices}} {{$t('common_content.of')}} {{salesInvoicesCount}}
          </div>
        </b-col>
        <b-col cols="7">
          <b-pagination size="md" :total-rows="salesInvoicesCount" :per-page="searchObject.limit"
                        v-model="currentPaginateIndex"
                        @input="search"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';

  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    handlerResponse,
    sumTotalData,
    setNotifyData,
    transformDateToString,
    transformDateFromDateRangeToStringFormatByIndex,
    getFileNameFromContentDisposition,
    isNull, addColIdToColumns, convertTotalDataToObject
  } from '@/helpers/commonHelpers';
  import {
    alignCells,
    autoResize, formatNumber,
    LinkCellRenderer,
    MultiLinkCellRenderer,
    notificationsRenderer
  } from '@/helpers/renderHelpers';

  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';
  import CreateSalesInvoiceModal from '@/components/modals/CreateSalesInvoiceModal';
  import {
    COMMISSION,
    CREDIT, DEBIT,
    EQUIPMENT,
    INVOICE,
    PARTNER,
    SERVICE
  } from '@/constants/invoiceTypes';

  import { existenceAutoCreated } from '@/helpers/sale-invoices-helpers';
  import { gridMixins } from '@/mixins';
  import gridProps from '@/gridProps';

  export default {
    mixins: [gridMixins],
    components: {
      Datepicker,
      Multiselect,
      'ag-grid-vue': AgGridVue,
      SaveColumnsBtn,
      CreateSalesInvoiceModal
    },
    data() {
      return {
        reportDate: null,
        selectedData: [
          {
            label: 'selected',
            value: 0
          }
        ],
        searchData: {
          user: [],
          status: [],
          buyers: [],
          sellers: [],
          paid_status_official: [],
          type: [],
          paid_status_additional: [],
          stock_addresses: [],
          buyer_country_code: [],
          invoice_number: '',
          paid_status: '',
          overdue: false,
          invoice_date: [],
          type_date: null,
          invoicing_type: []
        },
        searchObject: {
          limit: 100,
          offset: 0
        },
        paginationState: true,
        currentPaginateIndex: 1,
        templates: [],
        template: null,
        defaultGridTemplate: {
          params: [],
          filters: {}
        },
        gridNameForTemplates: 'invoice_search',
        excelReport: null,
        paymentStatuses: [
          {name: 'Not paid', key: 'notpaid'},
          {name: 'Partly paid', key: 'notfullpaid'},
          {name: 'Paid', key: 'paid'}],
        invoiceStatuses: [
          {key: 'draft', name: 'Draft'},
          {key: 'approved', name: 'Approved'}
        ],
        date_types: [
          {id: 'created_date', name: this.$t('selects.invoices.date_create')},
          {id: 'invoice_date', name: this.$t('selects.invoices.date_invoice')},
          {id: 'date_due', name: this.$t('selects.invoices.payment_date')},
          {id: 'paid_date', name: this.$t('selects.invoices.paid')}
        ],
        invoicing_types: [
          {id: 'single', name: this.$t('selects.invoices.direct_sale')},
          {id: 'commission', name: this.$t('selects.invoices.commission_sale')},
          {id: 'service', name: this.$t('selects.invoices.service_sale')}
        ],
        showCollapse: true,
        popoverCreateWithStatus: false,
        createInvoiceData: {},
        storageKey: 'salesInvoices',
        gridOptions: {
          rowSelection: 'multiple',
          rowGroupPanelShow: 'always',
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            enableRowGroup: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            },
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          ...gridProps
        },
        salesInvoices: [],
        salesInvoicesColumns: [],
        salesInvoicesGridName: '',
        salesInvoicesTotalData: [],
        salesInvoicesCount: 0
      }
    },
    computed: {
      ...mapState('generic', ['currentRoutePermissions']),
      ...mapState('accounting', ['salesInvoicesExports', 'independent_si_types', 'si_types']),
      ...mapState('generic', ['invoicesGridTemplates']),
      ...mapState('woodware', ['countries', 'users']),
      ...mapGetters('woodware', ['buyers', 'sellers', 'stock_addresses']),
      gridHeight() {
        return this.showCollapse ? {height: 'calc(100vh - 550px)'} : {height: 'calc(100vh - 305px)'};
      },
      countOfViewSalesInvoices() {
        return this.salesInvoices.length;
      },
      isTypeDateEmpty() {
        return this.searchData.type_date === undefined || isNull(this.searchData.type_date);
      },
      salesInvoicesGridOptions() {
        return {
          ..._.cloneDeep(this.gridOptions),
          components: {
            'SalesInvoiceLink': LinkCellRenderer('id', '/sales-invoice/'),
            'BuyerLink': LinkCellRenderer('buyer_id', '/partners/'),
            'PaymentsLink': MultiLinkCellRenderer('payment_numbers', '/payments/', 'payment_number', 'id')
          },
          defaultColDef: {
            ...this.gridOptions.defaultColDef,
            enableValue: true,
            enableRowGroup: true,
            valueFormatter(params) {
              return params.column.colId.includes('m3') || params.column.colId.includes('deal_margin_percent')
                  ? formatNumber(params.value, 3)
                  : params.column.colId.includes('delay_of_payment')
                      ? formatNumber(params.value, 0)
                      : formatNumber(params.value, 2);
            },
            cellStyle(params) {
              return alignCells(params);
            },
          },
        }
      }
    },
    methods: {
      create(type) {
        if (type === INVOICE || type === SERVICE || type === COMMISSION) {
          this.createInvoiceData = {
            title: this.$t('modal_dialogs.create_invoice'),
            isCouple: type !== INVOICE,
            type: type === INVOICE ? 'single' : type
          };
          this.$root.$emit('openCreateSalesInvoiceModal');
        }
        if (type === PARTNER) {
          this.$router.push({path: `/sales-invoice/new/partner_invoice`});
        }
        if (type === EQUIPMENT) {
          this.$router.push({path: `/sales-invoice/new/equipment_invoice`});
        }
        if (type === CREDIT) {
          this.$router.push({path: `/sales-invoice/new/credit_invoice`});
        }
        if (type === DEBIT) {
          this.$router.push({path: `/sales-invoice/new/debit_invoice`});
        }
        this.popoverCreateWithStatus = false;
      },
      async deleteInvoices() {
        const ids = getArrayByProperty(this.salesInvoicesGridOptions.api.getSelectedRows(), 'id');
        if (ids.length) {
          try {
            const notifyData = await this.$http.delete(`accounting/invoices/`, {body: {invoices_id: ids}});
            this.$notify(setNotifyData('', notifyData.body, 'success'));
            await this.search();
          } catch (e) {
            this.$notify(setNotifyData('Error', e.data.detail, 'error'));
          }
        } else {
          this.$notify(setNotifyData('Select invoices for removing', '', 'warn'));
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData, this.storageKey);
      },
      async gridReady() {
        await this.search();
        if (this.template) {
          this.applyTemplate(this.template);
        } else {
          await this.setDefaultGridTemplate();
        }
      },
      async search() {
        const params = this.prepareSearchData();

        // if (this.salesInvoicesGridOptions.api) {
        //   this.salesInvoicesGridOptions.api.showLoadingOverlay();
        // }
        const {data, used_columns, grid_name, total} = await this.$http.get('accounting/search_for_invoices/', {params})
          .then(handlerResponse);
        this.salesInvoicesColumns = used_columns
        this.salesInvoicesGridName = grid_name;
        this.salesInvoicesTotalData = convertTotalDataToObject(total);
        this.salesInvoicesCount = data.count;
        this.selectedData = sumTotalData([], this.selectedData);
        this.salesInvoices = data.results;
        saveFilters(this.storageKey, this.searchData);
      },
      prepareSearchData() {
        const searchData = {
          user: getArrayByProperty(this.searchData.user, 'id').toString(),
          status: getArrayByProperty(this.searchData.status, 'key').toString(),
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          sellers: getArrayByProperty(this.searchData.sellers, 'id').toString(),
          invoicing_type: getArrayByProperty(this.searchData.invoicing_type, 'id').toString(),
          paid_status_official: getArrayByProperty(this.searchData.paid_status_official, 'key').toString(),
          paid_status_additional: getArrayByProperty(this.searchData.paid_status_additional, 'key').toString(),
          type: getArrayByProperty(this.searchData.type, 'key').toString(),
          stock_addresses: getArrayByProperty(this.searchData.stock_addresses, 'id').toString(),
          buyer_country_code: getArrayByProperty(this.searchData.buyer_country_code, 'short_name').toString(),
          invoice_number: this.searchData.invoice_number && this.searchData.invoice_number.toString(),
          paid_status: this.searchData.paid_status && this.searchData.paid_status.toString(),
          overdue: this.searchData.overdue || null,
        };
        if (this.reportDate) {
          searchData.report_date = transformDateToString(this.reportDate);
        };
        const {limit} = this.searchObject;
        if (limit) {
          searchData.limit = limit;
          searchData.offset = (this.currentPaginateIndex - 1) * limit;
        }
        if (!this.isTypeDateEmpty) {
          const {id} = this.searchData.type_date;
          if (id === 'created_date') {
            searchData.created_date_between_after = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0);
            searchData.created_date_between_before = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1);
          }
          if (id === 'invoice_date') {
            searchData.invoice_date_between_after = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0);
            searchData.invoice_date_between_before = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1);
          }
          if (id === 'date_due') {
            searchData.date_due_between_after = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0);
            searchData.date_due_between_before = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1);
          }
          if (id === 'paid_date') {
            searchData.paid_date_between_after = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0);
            searchData.paid_date_between_before = transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1);
          }
        }
        return searchData;
      },
      selectionChangedInvoices() {
        const selectedInvoices = this.salesInvoicesGridOptions.api.getSelectedRows();
        this.selectedData = sumTotalData(selectedInvoices, this.selectedData);
      },
      redirectToInvoice(params) {
        if (params.data && params.data.id) {
          this.$router.push({path: `/sales-invoice/${params.data.id}`});
        }
      },
      async setDefaultGridTemplate() {
        if (this.salesInvoicesGridOptions.columnApi && this.salesInvoicesGridOptions.api) {
          this.defaultGridTemplate.params = this.salesInvoicesGridOptions.columnApi.getColumnState();
          this.defaultGridTemplate.filters = this.salesInvoicesGridOptions.api.getFilterModel();
        }
      },
      applyTemplate(currentTemplate) {
        if (currentTemplate) {
          this.salesInvoicesGridOptions.columnApi.setColumnState(currentTemplate.params);
          this.salesInvoicesGridOptions.api.setFilterModel(currentTemplate.filters);
        } else {
          this.salesInvoicesGridOptions.columnApi.setColumnState(this.defaultGridTemplate.params);
          this.salesInvoicesGridOptions.api.setFilterModel(this.defaultGridTemplate.filters);
        }
        this.$nextTick(() => {
          autoResize(this.salesInvoicesGridOptions);
        })
      },
      prepareDialog() {
        return {
          title: this.$i18n.t(`text_content.creating_grid_template`),
          input: 'text',
          inputPlaceholder: this.$i18n.t(`placeholders.enter_grid_template_name`),
          showCancelButton: true,
          cancelButtonText: this.$i18n.t(`buttons.common.cancel`)
        }
      },
      createTemplate() {
        this.$swal.fire(this.prepareDialog()).then(result => {
          const templateName = result.value;
          if (templateName) {
            const params = this.salesInvoicesGridOptions.columnApi.getColumnState(),
              name = templateName, filters = this.salesInvoicesGridOptions.api.getFilterModel(),
              grid_name = this.gridNameForTemplates;
            const savedData = {params, name, grid_name, filters};
            this.$http.post(`generic/pivot_grid_table_templates/`, savedData).then(handlerResponse).then((response) => {
              const templateName = response.name;
              this.$notify(setNotifyData(this.$i18n.t(`notifications.common.grid_template_created`, {templateName}), '', 'success'));
              this.$store.dispatch('generic/getGridTemplates', this.gridNameForTemplates);
            }).catch(error => notificationsRenderer(error.data, 'error'));
          }
        });
      },
      deleteSelectedTemplate() {
        const templateId = this.template.id, templateName = this.template.name;
        this.$http.delete(`generic/pivot_grid_table_templates/${templateId}/`).then(() => {
          this.template = null;
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.grid_template_deleted`, {templateName}), '', 'success'));
          this.$store.dispatch('generic/getGridTemplates', this.gridNameForTemplates);
        }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      updateSelectedTemplate() {
        const templateId = this.template.id, templateName = this.template.name,
          params = this.salesInvoicesGridOptions.columnApi.getColumnState(),
          grid_name = this.gridNameForTemplates, filters = this.salesInvoicesGridOptions.api.getFilterModel();
        const savedData = {grid_name, filters, params};
        this.$http.put(`generic/pivot_grid_table_templates/${templateId}/`, savedData).then(() => {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.grid_template_updated`, {templateName}), '', 'success'));
          this.$store.dispatch('generic/getGridTemplates', this.gridNameForTemplates);
        }).catch(error => notificationsRenderer(error.data, 'error'));
      },
      setQuickFilter(e) {
        this.salesInvoicesGridOptions.api.setQuickFilter(e.target.value);
      },
      async togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 100;
          this.searchObject.offset = 0;
        }
        await this.search();
      },
      selectSalesInvoicesReport(report) {
        const params = this.prepareSearchData();
        this.$http.get(`accounting/generate_invoice_report/${report.key}/`, {responseType: 'blob', params})
          .then((response) => {
            const disposition = response.headers.get('content-disposition'),
              fileName = getFileNameFromContentDisposition(disposition, report.value);
            saveAs(response.body, `${fileName}`);
            this.excelReport = null;
          }).catch(error => notificationsRenderer(error.body, 'error'));
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('accounting/loadIndependentSaleInvoicesTypes');
      this.$store.cache.dispatch('accounting/loadSaleInvoicesTypes');
      this.$store.dispatch('woodware/getPartners', {is_active: true});
      this.$store.cache.dispatch('woodware/getUsers');
      this.$store.dispatch('generic/getGridTemplates', this.gridNameForTemplates);
      this.$store.cache.dispatch('accounting/loadSalesInvoicesExports');
      this.$store.cache.dispatch('woodware/getCountries');
      this.$store.cache.dispatch('woodware/getStockAddresses');
      if (checkSavedFilterData(this.storageKey)) {
        this.searchData = setFilters(this.storageKey);
      }
    }
  }
</script>

<style lang="scss">
  .ag-cell-wrapper {
    display: flex;
  }

  .invoice-grid-templates {
    @media (min-width: 1024px) {
      width: 200px;
    }
    @media (min-width: 940px) and (max-width: 1023px) {
      width: 150px;
    }
    @media (max-width: 939px) {
      width: 100px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/sales/invoices/SalesInvoicesList.vue